import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { skipToken } from '@reduxjs/toolkit/query';
import { Section } from '@components/Section';
import { Button } from '@components/Button';
import { Loader } from '@components/Loader';
import {
  useAddNewAppointmentsMutation,
  useLazyGetAvailableSlotsQuery,
} from '@api/appointments/appointmentsApi';
import { useFetchAllPatientsWithoutPaginationQuery } from '@api/patients/patientsAPI';
import {
  useFetchAllTrialsWithoutPaginationQuery,
  useFetchTrialByIdQuery,
} from '@api/trial/trialAPI';
import { useFetchAllLocationsWithoutPaginationQuery } from '@api/locations/locationsAPI';
import { useFetchUserByIdQuery } from '@api/user/userAPI';
import { useLanguageContext } from '@hooks/useLanguageContext';
import { errorAlert, successAlert } from '@helpers/alerts/alerts';
import { timeToTimestampFormatter } from '@helpers/timeToTimestampFormatter';
import { calculateAge } from '@helpers/calculateAge';
import { ModalTypes } from '@components/Confirm/types';
import { useConfirmationModal } from '@components/Confirm/context/useConfirmContext';
import { IAvailableSlot, ICreateMultiAppointment } from '@api/appointments/types';
import { Routes } from '../../../../../router';
import { StepIndicator } from '../components/StepIndicator';
import { PatientTrialStep } from '../components/PatientTrialStep';
import { VisitSelectionStep } from '../components/VisitSelectionStep';
import { SlotPickerStep } from '../components/SlotPickerStep';
import { NotificationSettingsStep } from '../components/NotificationSettingsStep';
import { ReviewConfirmStep } from '../components/ReviewConfirmStep';
import { IRawVisit, IVisitTemplate, IVisitSelection, MultiAppointmentStep } from '../types';
import '../styles.scss';

export const MultiAppointmentForm: FC = () => {
  const { dictionary } = useLanguageContext();

  const navigate = useNavigate();

  const { openConfirmationModal } = useConfirmationModal();

  const [currentStep, setCurrentStep] = useState(MultiAppointmentStep.PatientTrial);

  const [trialId, setTrialId] = useState('');

  const [patientId, setPatientId] = useState('');

  const [locationId, setLocationId] = useState('');

  const [physicianId, setPhysicianId] = useState('');

  const [contactPhone, setContactPhone] = useState('');

  const [visitTemplates, setVisitTemplates] = useState<IVisitTemplate[]>([]);

  const [selectedVisits, setSelectedVisits] = useState<number[]>([]);

  const [visitSelections, setVisitSelections] = useState<Map<number, IVisitSelection>>(new Map());

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isSuccess, setIsSuccess] = useState(false);

  const { data: patients } = useFetchAllPatientsWithoutPaginationQuery();

  const { data: trials } = useFetchAllTrialsWithoutPaginationQuery();

  const { data: locations } = useFetchAllLocationsWithoutPaginationQuery();

  const { data: trial, isLoading: trialLoading } = useFetchTrialByIdQuery(trialId || skipToken, {
    refetchOnMountOrArgChange: true,
  });

  const { data: patient } = useFetchUserByIdQuery(patientId || skipToken, {
    refetchOnMountOrArgChange: true,
  });

  const [addAppointment] = useAddNewAppointmentsMutation();

  const [fetchSlots] = useLazyGetAvailableSlotsQuery();

  const trialOptions = useMemo(
    () =>
      trials?.data.results.map(({ protocolName: label, id: value, physicians }) => ({
        label,
        value,
        locationId: physicians?.schedule?.locationId ?? null,
      })) || [],
    [trials],
  );

  const patientOptions = useMemo(
    () =>
      patients?.data.results.map(({ name, surname, id }) => ({
        label: `${name} ${surname}`,
        value: id,
      })) || [],
    [patients],
  );

  const locationOptions = useMemo(
    () =>
      locations?.data.results.map(({ name: label, id: value }) => ({
        label,
        value: String(value),
      })) || [],
    [locations],
  );

  useEffect(() => {
    if (trial?.data) {
      const template = trial.data.appointmentTemplate;

      if (template) {
        const trialProtocolWindow = trial.data.protocolWindow ? Number(trial.data.protocolWindow) : undefined;

        const visits: IVisitTemplate[] = (Object.values(template) as IRawVisit[]).map((raw, idx: number) => ({
          index: idx,
          name: raw.name || '',
          type: raw.type || '',
          md: !!raw.md,
          duration: Number(raw.duration) || 30,
          time: raw.time || '',
          date: raw.date ? Number(raw.date) : undefined,
          protocolWindow: raw.protocolWindow ? Number(raw.protocolWindow) : trialProtocolWindow,
          notificationsSettings: {
            types: {
              sms: raw.notificationsSettings?.types?.sms || false,
              email: raw.notificationsSettings?.types?.email || true,
            },
            first: raw.notificationsSettings?.first || 1440,
            second: raw.notificationsSettings?.second,
          },
        }));

        setVisitTemplates(visits);
        setSelectedVisits(visits.map((_, i) => i));
      }

      if (trial.data.physicians?.phone) {
        setContactPhone(trial.data.physicians.phone);
      }
      if (trial.data.physicians?.schedule?.locationId) {
        setLocationId(trial.data.physicians.schedule.locationId);
      }
      if (trial.data.physicianId) {
        setPhysicianId(trial.data.physicianId);
      }
    }
  }, [trial]);

  const handleTrialChange = useCallback((value: string) => {
    setTrialId(value);
    setVisitSelections(new Map());
    setCurrentStep(MultiAppointmentStep.PatientTrial);
    // Avoid stale trial-derived fields while the new trial is loading (prevents wrong Continue state).
    setLocationId('');
    setContactPhone('');
    setPhysicianId('');
    setVisitTemplates([]);
    setSelectedVisits([]);
  }, []);

  const handlePatientChange = useCallback((value: string) => {
    setPatientId(value);
  }, []);

  const handleLocationChange = useCallback((value: string) => {
    setLocationId(value);
  }, []);

  const handleVisitToggle = useCallback((index: number) => {
    setSelectedVisits((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      }
      return [...prev, index].sort((a, b) => a - b);
    });
    setVisitSelections(new Map());
  }, []);

  const handleClearSlot = useCallback((visitIndex: number) => {
    setVisitSelections((prev) => {
      const next = new Map(prev);

      next.delete(visitIndex);
      return next;
    });
  }, []);

  const handleNotificationSettingsChange = useCallback(
    (visitIndex: number, settings: IVisitSelection['notificationsSettings']) => {
      setVisitSelections((prev) => {
        const next = new Map(prev);

        const existing = next.get(visitIndex);

        if (existing) {
          next.set(visitIndex, {
            ...existing,
            notificationsSettings: settings,
          });
        }

        return next;
      });
    },
    [],
  );

  const handleFetchSlots = useCallback(
    async (visitIndex: number, date: string) => {
      const visit = visitTemplates.find((v) => v.index === visitIndex);

      if (!visit || !trialId || !locationId || !physicianId) return [];

      try {
        const result = await fetchSlots({
          trialId,
          locationId,
          physicianId,
          date,
          duration: visit.duration,
          md: visit.md,
        }).unwrap();

        let slots = result.data || [];

        const [year, month, day] = date.split('-');

        const selectedDate = new Date(Number(year), Number(month) - 1, Number(day));

        const now = new Date();

        const isToday =
          selectedDate.getFullYear() === now.getFullYear() &&
          selectedDate.getMonth() === now.getMonth() &&
          selectedDate.getDate() === now.getDate();

        if (isToday) {
          const currentMinutes = now.getHours() * 60 + now.getMinutes();

          slots = slots.filter((slot) => {
            const [h, m] = slot.from_time.split(':').map(Number);

            return h * 60 + m > currentMinutes;
          });
        }

        return slots;
      } catch {
        errorAlert('Error', 'Failed to fetch available slots');
        return [];
      }
    },
    [visitTemplates, trialId, locationId, physicianId, fetchSlots],
  );

  const isSingleVisit = visitTemplates.length === 1;

  const canProceedToStep = useCallback(
    (step: MultiAppointmentStep) => {
      switch (step) {
        case MultiAppointmentStep.PatientTrial:
          return true;
        case MultiAppointmentStep.VisitSelection:
          return !!trialId && !!patientId && !!locationId && !trialLoading;
        case MultiAppointmentStep.SlotPicker:
          if (visitTemplates.length === 1) {
            return !!trialId && !!patientId && !!locationId && !trialLoading;
          }
          return selectedVisits.length > 0 && !trialLoading;
        case MultiAppointmentStep.NotificationSettings:
          return selectedVisits.every((idx) => visitSelections.has(idx));
        case MultiAppointmentStep.ReviewConfirm:
          return selectedVisits.every((idx) => {
            const selection = visitSelections.get(idx);

            return selection && selection.notificationsSettings;
          });
        default:
          return false;
      }
    },
    [trialId, patientId, locationId, selectedVisits, visitSelections, visitTemplates, trialLoading],
  );

  const goToStep = useCallback(
    (step: MultiAppointmentStep) => {
      if (step <= currentStep || canProceedToStep(step)) {
        setCurrentStep(step);
      }
    },
    [currentStep, canProceedToStep],
  );

  const handleSlotSelect = useCallback(
    (visitIndex: number, slot: IAvailableSlot, date: string) => {
      setVisitSelections((prev) => {
        const next = new Map(prev);

        next.set(visitIndex, {
          visitIndex,
          selectedSlot: slot,
          selectedDate: date,
        });

        return next;
      });
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const isMinor = patient?.dateOfBirth && calculateAge(patient.dateOfBirth);

      const adult = isMinor ? 0 : 1;

      const appointmentSettings = selectedVisits.map((idx) => {
        const visit = visitTemplates.find((v) => v.index === idx);

        const selection = visitSelections.get(idx);

        if (!visit || !selection?.selectedSlot) throw new Error('Missing selection');

        const [year, month, day] = selection.selectedDate.split('-');

        const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));

        const displayFrom = selection.selectedSlot.display_from;

        const displayTo = selection.selectedSlot.display_to;

        const fromTimeHHMM = selection.selectedSlot.from_time;

        const toTimeHHMM = selection.selectedSlot.to_time;

        return {
          name: visit.name || visit.type,
          type: visit.type,
          md: visit.md,
          duration: visit.duration,
          date: format(parsedDate, 'MM/dd/yyyy'),
          time: `${displayFrom}-${displayTo}`,
          from: timeToTimestampFormatter(displayFrom, parsedDate).toISOString(),
          to: timeToTimestampFormatter(displayTo, parsedDate).toISOString(),
          fromTime: fromTimeHHMM,
          toTime: toTimeHHMM,
          notificationsSettings: selection.notificationsSettings || visit.notificationsSettings,
        };
      });

      const result = await addAppointment({
        trialId,
        patientId,
        contactPhone: contactPhone.replace(/[-()\s]/g, ''),
        locationId,
        physicianId,
        adult,
        appointmentSettings,
      } as ICreateMultiAppointment).unwrap();

      if (result.success === 1) {
        const count = selectedVisits.length;

        const patientName = patientOptions.find((p) => p.value === patientId)?.label || 'the patient';

        const successMessage = count === 1
          ? `Appointment for ${patientName} has been scheduled successfully`
          : `All ${count} appointments for ${patientName} have been scheduled successfully`;

        successAlert(successMessage);
        setIsSuccess(true);
      } else {
        errorAlert(result.message);
      }
    } catch (err: unknown) {
      const message = (err as { data?: { message?: string } })?.data?.message || 'Failed to create appointments';

      errorAlert(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    selectedVisits,
    visitTemplates,
    visitSelections,
    trialId,
    patientId,
    patientOptions,
    contactPhone,
    locationId,
    physicianId,
    patient,
    addAppointment,
  ]);

  const onCancelClick = () =>
    openConfirmationModal({
      variant: ModalTypes.decline,
      title: dictionary.appointments.modals.cancel.title,
      text: dictionary.appointments.modals.cancel.text,
      submitButtonTitle: dictionary.table.ok,
      handleConfirmClick: () => navigate(Routes.Appointments),
    });

  const selectedTrialLabel = trialOptions.find((t) => t.value === trialId)?.label || '';

  const selectedPatientLabel = patientOptions.find((p) => p.value === patientId)?.label || '';

  const selectedLocationLabel = locationOptions.find((l) => l.value === locationId)?.label || '';

  const sectionTitle = 'Schedule Trial Appointments';

  const omitSteps = isSingleVisit ? new Set([MultiAppointmentStep.VisitSelection]) : undefined;

  // Full-screen loader only past step 1; on Patient/Trial step it replaced the whole form and caused a flash.
  const trialDetailLoading = Boolean(trialId) && trialLoading;

  if (trialDetailLoading && currentStep !== MultiAppointmentStep.PatientTrial) {
    return <Loader />;
  }

  if (isSuccess) {
    return (
      <Section title={selectedVisits.length === 1 ? 'Appointment Scheduled' : 'Appointments Scheduled'}>
        <div className="form-section multi-appointment-success">
          <div className="multi-appointment-success-icon">✓</div>
          <h2 className="title-lg multi-appointment-success-title">
            {selectedVisits.length === 1 ? 'Appointment Scheduled' : 'All Appointments Scheduled'}
          </h2>
          <p className="text-md multi-appointment-success-text">
            {selectedVisits.length === 1 ? '1 appointment' : `${selectedVisits.length} appointments`} for{' '}
            <strong>{selectedPatientLabel}</strong> in trial{' '}
            <strong>{selectedTrialLabel}</strong> {selectedVisits.length === 1 ? 'has' : 'have'} been created with Pending status.
          </p>
          <div className="multi-appointment-success-actions">
            <Button
              label="Schedule Another"
              handleClick={() => {
                setIsSuccess(false);
                setCurrentStep(MultiAppointmentStep.PatientTrial);
                setTrialId('');
                setPatientId('');
                setVisitSelections(new Map());
              }}
            />
            <Button
              label="View Appointments"
              variant="default"
              handleClick={() => navigate(Routes.Appointments)}
            />
          </div>
        </div>
      </Section>
    );
  }

  return (
    <Section title={sectionTitle}>
      <div className="multi-appointment">
      <StepIndicator
        currentStep={currentStep}
        onStepClick={goToStep}
        canProceed={canProceedToStep}
        omitSteps={omitSteps}
      />

      {currentStep === MultiAppointmentStep.PatientTrial && (
        <div className="form-body">
          <PatientTrialStep
            trialId={trialId}
            patientId={patientId}
            locationId={locationId}
            contactPhone={contactPhone}
            trialOptions={trialOptions}
            patientOptions={patientOptions}
            locationOptions={locationOptions}
            onTrialChange={handleTrialChange}
            onPatientChange={handlePatientChange}
            onLocationChange={handleLocationChange}
            onContactPhoneChange={setContactPhone}
          />
          <section className="form-section form-footer">
            <Button
              label={dictionary.actions.cancel}
              variant="default"
              handleClick={onCancelClick}
              size="static"
            />
            <Button
              label={isSingleVisit ? 'Find Available Slots' : 'Continue'}
              size="static"
              isDisabled={!canProceedToStep(
                isSingleVisit ? MultiAppointmentStep.SlotPicker : MultiAppointmentStep.VisitSelection,
              )}
              handleClick={() =>
                goToStep(
                  isSingleVisit ? MultiAppointmentStep.SlotPicker : MultiAppointmentStep.VisitSelection,
                )
              }
            />
          </section>
        </div>
      )}

      {currentStep === MultiAppointmentStep.VisitSelection && (
        <div className="form-body">
          <VisitSelectionStep
            visitTemplates={visitTemplates}
            selectedVisits={selectedVisits}
            trialName={selectedTrialLabel}
            onVisitToggle={handleVisitToggle}
          />
          <section className="form-section form-footer">
            <Button
              label="Back"
              variant="default"
              size="static"
              handleClick={() => goToStep(MultiAppointmentStep.PatientTrial)}
            />
            <Button
              label="Find Available Slots"
              size="static"
              isDisabled={!canProceedToStep(MultiAppointmentStep.SlotPicker)}
              handleClick={() => goToStep(MultiAppointmentStep.SlotPicker)}
            />
          </section>
        </div>
      )}

      {currentStep === MultiAppointmentStep.SlotPicker && (
        <div className="form-body">
          <SlotPickerStep
            visitTemplates={visitTemplates.filter((_, i) => selectedVisits.includes(i))}
            selectedVisitIndices={selectedVisits}
            visitSelections={visitSelections}
            onSlotSelect={handleSlotSelect}
            onClearSlot={handleClearSlot}
            onFetchSlots={handleFetchSlots}
          />
          <section className="form-section form-footer">
            <Button
              label="Back"
              variant="default"
              size="static"
              handleClick={() =>
                goToStep(
                  isSingleVisit ? MultiAppointmentStep.PatientTrial : MultiAppointmentStep.VisitSelection,
                )
              }
            />
            <Button
              label="Continue to Notification Settings"
              size="static"
              isDisabled={!canProceedToStep(MultiAppointmentStep.NotificationSettings)}
              handleClick={() => goToStep(MultiAppointmentStep.NotificationSettings)}
            />
          </section>
        </div>
      )}

      {currentStep === MultiAppointmentStep.NotificationSettings && (
        <div className="form-body">
          <NotificationSettingsStep
            visitTemplates={visitTemplates.filter((_, i) => selectedVisits.includes(i))}
            selectedVisitIndices={selectedVisits}
            visitSelections={visitSelections}
            onNotificationSettingsChange={handleNotificationSettingsChange}
          />
          <section className="form-section form-footer">
            <Button
              label="Back"
              variant="default"
              size="static"
              handleClick={() => goToStep(MultiAppointmentStep.SlotPicker)}
            />
            <Button
              label="Review Appointments"
              size="static"
              isDisabled={!canProceedToStep(MultiAppointmentStep.ReviewConfirm)}
              handleClick={() => goToStep(MultiAppointmentStep.ReviewConfirm)}
            />
          </section>
        </div>
      )}

      {currentStep === MultiAppointmentStep.ReviewConfirm && (
        <div className="form-body">
          <ReviewConfirmStep
            visitTemplates={visitTemplates}
            selectedVisits={selectedVisits}
            visitSelections={visitSelections}
            patientName={selectedPatientLabel}
            trialName={selectedTrialLabel}
            locationName={selectedLocationLabel}
            isSubmitting={isSubmitting}
            isSingleVisit={isSingleVisit}
          />
          <section className="form-section form-footer">
            <Button
              label="Back"
              variant="default"
              size="static"
              handleClick={() => goToStep(MultiAppointmentStep.NotificationSettings)}
            />
            <Button
              label={isSingleVisit ? 'Confirm & Schedule Appointment' : `Confirm & Schedule ${selectedVisits.length} Appointments`}
              variant="success"
              size="static"
              isDisabled={isSubmitting}
              handleClick={handleSubmit}
            />
          </section>
        </div>
      )}
      </div>
    </Section>
  );
};
